/**
 * Image attachments for expenses (receipt photos and screenshots). Pure rules only: which files
 * are accepted and how an image is prepared before it is stored locally. The pixels are processed
 * in the browser (src/integrations/images); nothing is ever uploaded.
 *
 * Storage policy — one stored copy per image, OCR-friendly:
 *  - JPEG (camera photos) is always re-encoded: this applies the EXIF rotation to the pixels and
 *    drops EXIF metadata such as GPS position.
 *  - Anything larger than 2560 px on its long edge is scaled down (never up), keeping proportions.
 *    2560 px keeps receipt text well above the resolution OCR needs.
 *  - Small PNG/WebP files (typically screenshots) are kept byte-for-byte: re-encoding sharp UI text
 *    as JPEG would only add artifacts.
 *  - Everything else (HEIC, GIF, BMP, oversized PNG/WebP) is re-encoded as JPEG, quality 0.88.
 *  - The original file itself is not kept; its name, type, size and dimensions are recorded.
 */

export const IMAGE_LIMITS = {
  /** Largest file accepted from the picker. */
  maxInputBytes: 25 * 1024 * 1024,
  /** Long edge of the stored image. */
  maxLongEdge: 2560,
  /** PNG/WebP up to this size (and within the long edge) are stored unchanged. */
  keepOriginalMaxBytes: 1.5 * 1024 * 1024,
  outputType: "image/jpeg",
  outputQuality: 0.88,
} as const;

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif", "image/bmp"] as const;
const EXTENSION_TYPES: Record<string, (typeof ACCEPTED_IMAGE_TYPES)[number]> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  gif: "image/gif",
  bmp: "image/bmp",
};

/** How the image entered the app: the "Fotografa ricevuta" camera button or the gallery/files picker. */
export const ATTACHMENT_ORIGINS = ["camera", "library"] as const;
export type AttachmentOrigin = (typeof ATTACHMENT_ORIGINS)[number];

export class UnsupportedImageError extends Error {
  override readonly name = "UnsupportedImageError";
}

export interface FileMeta {
  name: string;
  type: string;
  size: number;
}

/**
 * Resolves the image type of a picked file, or throws UnsupportedImageError. Some pickers report
 * an empty type (e.g. HEIC on desktop); the file extension is used as a fallback.
 */
export function validateImageFile(file: FileMeta): (typeof ACCEPTED_IMAGE_TYPES)[number] {
  const declared = file.type.toLowerCase();
  const byExtension = EXTENSION_TYPES[file.name.split(".").pop()?.toLowerCase() ?? ""];
  const type = (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(declared) ? (declared as (typeof ACCEPTED_IMAGE_TYPES)[number]) : declared === "" ? byExtension : undefined;
  if (!type) {
    throw new UnsupportedImageError("Il file scelto non è un'immagine supportata. Usa una foto o uno screenshot (JPEG, PNG, WebP o HEIC).");
  }
  if (file.size === 0) throw new UnsupportedImageError("Il file è vuoto.");
  if (file.size > IMAGE_LIMITS.maxInputBytes) {
    throw new UnsupportedImageError(`L'immagine è troppo grande (${Math.round(file.size / 1048576)} MB, massimo 25 MB).`);
  }
  return type;
}

export interface ProcessingPlan {
  action: "keep" | "resize" | "reencode";
  width: number;
  height: number;
  outputType: string;
}

/** Decides how an image is stored, from its type, byte size and decoded dimensions. */
export function planImageProcessing(input: { type: string; size: number; width: number; height: number }): ProcessingPlan {
  const longEdge = Math.max(input.width, input.height);
  if (longEdge > IMAGE_LIMITS.maxLongEdge) {
    const scale = IMAGE_LIMITS.maxLongEdge / longEdge;
    return {
      action: "resize",
      width: Math.max(1, Math.round(input.width * scale)),
      height: Math.max(1, Math.round(input.height * scale)),
      outputType: IMAGE_LIMITS.outputType,
    };
  }
  const losslessScreenshot = (input.type === "image/png" || input.type === "image/webp") && input.size <= IMAGE_LIMITS.keepOriginalMaxBytes;
  if (losslessScreenshot) return { action: "keep", width: input.width, height: input.height, outputType: input.type };
  return { action: "reencode", width: input.width, height: input.height, outputType: IMAGE_LIMITS.outputType };
}
